import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, Card, Typography, Button, Grid, FormControl, InputLabel, TextField, Select, MenuItem } from '@mui/material';
import useAuth from '../../../hooks/useAuth';
import { LoadingButton } from '@mui/lab';
import axiosInstance from '../../../api/axios';
import { Close, AttachMoney } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { notifySuccess, notifyError } from "../../../components/common/toast/ToastFunc";
import Loading from '../../../components/common/loading/Loading';
import TransactionOTPDialog from '../../../components/common/otp-input/TransactionOTPDialog';

const TransferPayment = () => {

  const { user } = useAuth();

  const isMerchant = user.role === 'merchant';

  const [account, setAccount] = useState({ type: '', no: '', amount: 0 });
  const [transactionType, setTransactionType] = useState('transfer');
  const [toAcc, setToAcc] = useState('');
  const [memo, setMemo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [reFetch, setReFetch] = useState(false);
  const [showTransactionOTPDialog, setShowTransactionOTPDialog] = useState(false);

  const theme = useTheme();

  const buttonTheme = useMemo(() => {
    return {
      color: 'white', backgroundColor: theme.mainColor, fontSize: 15, fontWeight: "bold", borderRadius: 2,
      '&:hover': {
        backgroundColor: '#b30000', color: 'white', fontSize: 15, fontWeight: "bold", borderRadius: 2
      },
    }
  }, [theme.mainColor]);

  const isMerchantPayment = useMemo(() => {
    return transactionType === 'payment' && isMerchant;
  }, [isMerchant, transactionType]);

  const handleCancelClick = () => {
    setAccount({ type: '', no: '', amount: 0 });
    setToAcc('');
    setMemo('');
  };

  const handleAmountChange = (event) => {
    try {
      const newAmount = event.target.value.replace(/^0+/, '') || '0'; // Remove leading zeros
      setAccount(prev => ({
        ...prev,
        amount: newAmount
      }));
    } catch (error) {
      notifyError("Something went wrong");
    }
  };

  const currentBalance = useMemo(() => {
    try {
      if (data && account["type"] && account["no"]) {
        const matchedAccount = data[account["type"]].find(acc => acc.account_no === account["no"]);
        return matchedAccount ? parseFloat(matchedAccount.account_balance) : 0;
      }
      return 0;
    } catch (error) {
      notifyError("Something went wrong");
    }
  }, [account, data]);

  const updatedBalance = useMemo(() => {
    try {
      if (!data) return 0;
      if (isMerchantPayment) {
        return currentBalance + parseFloat(account["amount"]);
      } else {
        return currentBalance - parseFloat(account["amount"]);
      }
    } catch (error) {
      notifyError("Something went wrong");
    }
  }, [account, currentBalance, data, isMerchantPayment]);

  const isTransactionValid = transactionType === 'transfer' ? (parseFloat(account["amount"]) <= parseFloat(currentBalance) && parseFloat(account["amount"]) > 0) : parseFloat(account["amount"]) > 0;

  const submitTransaction = useCallback(async (otp, otp_source) => {
    try {
      setTransferLoading(true);
      let response;
      if (isMerchantPayment) {
        response = await axiosInstance.post('/transaction/collect-request',
          {
            "merchant_account": account["no"],
            "customer_account": toAcc,
            "amount": parseFloat(account["amount"]),
            "otp": otp,
            "otp_source": otp_source
          }
        );
      }
      else {
        response = await axiosInstance.post('/transaction/create-transaction',
          {
            "from_acc": account["no"],
            "to_acc": toAcc,
            "amount": parseFloat(account["amount"]),
            "type": transactionType,
            "otp": otp,
            "otp_source": otp_source
          }
        );
      }
      notifySuccess(response?.data?.message || "Success");
      setShowTransactionOTPDialog(false);
      handleCancelClick();
      setReFetch(!reFetch);
    } catch (error) {
      notifyError(error.message || "Something went wrong");
    } finally {
      setTransferLoading(false);
    }
  }, [account, isMerchantPayment, reFetch, toAcc, transactionType]);

  const formatData = useCallback((inputData) => {
    try {
      const convertedData = {
        Checking: [],
        Savings: []
      };
      inputData[0].forEach(account => {
        if (account.type === "CHECKING") {
          convertedData.Checking.push({
            account_no: account.id.replace(/-/g, ''),
            account_balance: account.balance,
          });
        } else if (account.type === "SAVINGS") {
          convertedData.Savings.push({
            account_no: account.id.replace(/-/g, ''),
            account_balance: account.balance,
          });
        }
      });
      setData(convertedData);
    } catch (error) {
      notifyError("Something went wrong");
    }
  }, []);

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        setData(null);
        const response = await axiosInstance.get('/customer/account-summary/get-data');
        formatData(response.data.data);
      } catch (error) {
        notifyError(error.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    }
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reFetch]);

  return (
    <>
      {showTransactionOTPDialog && <TransactionOTPDialog onVerified={submitTransaction} transferLoading={transferLoading} setTransferLoading={setTransferLoading} showTransactionOTPDialog={showTransactionOTPDialog} setShowTransactionOTPDialog={setShowTransactionOTPDialog} otpSource={"Transaction"} />}
      {loading && <Box mt={5}><Loading /></Box>}
      {data &&
        <Card sx={{ p: 3, mt: 5, borderRadius: "7px", maxWidth: 350, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography align="center" variant="h5" sx={{ mb: 3, color: theme.mainColor }}>Fund Transfer or Payment</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Account Type</InputLabel>
                <Select
                  value={account.type}
                  onChange={(e) => setAccount({
                    "type": e.target.value,
                    "no": '',
                    "amount": 0
                  })}
                  label="Account Type"
                >
                  {data['Checking'].length > 0 && <MenuItem value={"Checking"}>Checking</MenuItem>}
                  {data['Savings'].length > 0 && <MenuItem value={"Savings"}>Savings</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{isMerchantPayment ? "To Account" : "From Account"}</InputLabel>
                <Select
                  value={account.no}
                  onChange={(e) => setAccount((prev) => ({
                    ...prev,
                    "no": e.target.value,
                    "amount": 0
                  }))}
                  label={isMerchantPayment ? "To Account" : "From Account"}
                >
                  {account["type"] && data[account["type"]].map((account) => (
                    <MenuItem key={account["account_no"]} value={account["account_no"]}>{account["account_no"]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} my={1}>
              <Typography>Current Balance: $ {parseFloat(currentBalance).toFixed(2)}</Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  value={transactionType}
                  onChange={(e) => setTransactionType(e.target.value)}
                  label="Transaction Type"
                >
                  <MenuItem value="transfer">Transfer</MenuItem>
                  <MenuItem value="payment">Payment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={isMerchantPayment ? "From Account" : "To Account"}
                variant="outlined"
                name="to_acc"
                value={toAcc}
                onChange={(event) => setToAcc(event.target.value)}
              />
            </Grid>
            {/* Amount Input */}
            <Grid item xs={12}>
              <TextField
                label="Amount"
                type="number" // Keep as text to manage input format
                InputProps={{
                  startAdornment: <AttachMoney />,
                }}
                fullWidth
                disabled={!account["no"]}
                value={account["amount"]}
                onChange={handleAmountChange}
              />
            </Grid>
            <Grid item xs={12} my={1}>
              <Typography>Updated Balance: $ {updatedBalance}</Typography>
            </Grid>
            {/* Memo Field */}
            <Grid item xs={12}>
              <TextField
                label="Memo"
                fullWidth
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, width: '100%' }}>
            <Button onClick={() => handleCancelClick()} startIcon={<Close />} sx={{ mr: 1, color: theme.mainColor }}>Cancel</Button>
            <LoadingButton
              variant="contained"
              onClick={() => setShowTransactionOTPDialog(true)}
              disabled={!toAcc || !isTransactionValid}
              loading={transferLoading}
              sx={{ ...buttonTheme }}
            >
              {transactionType}
            </LoadingButton>
          </Box>
        </Card >
      }
    </>
  );
};

export default TransferPayment;